/*
  MORE INFO IN UNICO DOCS
  https://devcenter.unico.io/unico-idcloud/integracao/integracao-by-unico/api/api-reference/criacao-do-processo
*/

export enum Flow {
  idlive = "idlive",
  id = "id",
  idlivetrust = "idlivetrust",
  idcheck = "idcheck",
  iddocs = "iddocs",
  idsign = "idsign",
  iddocsign = "iddocsign",
  idchecktrust = "idchecktrust",
  idchecktrustdocs = "idchecktrustdocs",
  idchecktrustsign = "idchecktrustsign",
  idchecktrustdocssign = "idchecktrustdocssign",
  idcheckserpro = "idcheckserpro",
  idcheckserprodocs = "idcheckserprodocs",
  idcheckserprodocssign = "idcheckserprodocssign",
  creditoconsignado = "creditoconsignado",
  idtrust = "idtrust",
  idunicodocs = "idunicodocs",
  idunicosign = "idunicosign",
  idunicodocssign = "idunicodocssign",
  idunicoserprodocssign = "idunicoserprodocssign",
  idtrustdocs = "idtrustdocs",
  idtrustsign = "idtrustsign",
  idtrustdocssign = "idtrustdocssign",
  idtoken = "idtoken",
  idtokentrust = "idtokentrust",
  idtokensign = "idtokensign",
  idsmart = "idsmart"
}

export enum UseCase {
  USE_CASE_LOGIN = "USE_CASE_LOGIN",
  USE_CASE_IDENTITY_REVALIDATION_30_MINUTES = "USE_CASE_IDENTITY_REVALIDATION_30_MINUTES",
  USE_CASE_IDENTITY_REVALIDATION_7_DAYS = "USE_CASE_IDENTITY_REVALIDATION_7_DAYS",
  USE_CASE_ACC_MANAGEMENT = "USE_CASE_ACC_MANAGEMENT",
  USE_CASE_SENSITIVE_ACC_MANAGEMENT = "USE_CASE_SENSITIVE_ACC_MANAGEMENT",
  USE_CASE_FIN_TRANSACTIONS = "USE_CASE_FIN_TRANSACTIONS",
  USE_CASE_SENSITIVE_FIN_TRANSACTIONS = "USE_CASE_SENSITIVE_FIN_TRANSACTIONS",
  USE_CASE_SENSITIVE_FIN_SERVICES = "USE_CASE_SENSITIVE_FIN_SERVICES",
  USE_CASE_CUSTOM = "USE_CASE_CUSTOM"
}

export enum Purpose {
  creditprocess = "creditprocess",
  biometryonboarding = "biometryonboarding",
  carpurchase = "carpurchase"
}

export enum DuiType {
  DUI_TYPE_BR_CPF = "DUI_TYPE_BR_CPF",
  DUI_TYPE_MX_CURP = "DUI_TYPE_MX_CURP",
  DUI_TYPE_US_SSN = "DUI_TYPE_US_SSN"
}

enum NotificationChannel {
  NOTIFICATION_CHANNEL_WHATSAPP = "NOTIFICATION_CHANNEL_WHATSAPP",
  NOTIFICATION_CHANNEL_SMS = "NOTIFICATION_CHANNEL_SMS"
}

export type UnicoCreateProcessDTO = {
  callbackUri: string;
  flow: Flow;
  flow_config?: Flowconfig;
  clientReference?: string;
  companyBranchId?: string;
  bioTokenId?: string;
  useCase?: UseCase;
  person?: Person;
  purpose: Purpose;
  expiresIn?: string;
  contextualization?: Contextualization;
  payload?: Payload[];
};

interface Payload {
  envelopePayload: EnvelopePayload;
}

interface EnvelopePayload {
  documents: Document[];
}

interface Document {
  documentName: string;
  fileContents: string;
}

interface Contextualization {
  currency: string;
  price: number;
  locale: Locale;
}

interface Locale {
  ptBr: PtBr;
}

interface PtBr {
  reason: string;
}

interface Person {
  duiType: DuiType;
  duiValue: string;
  friendlyName?: string;
  phone?: string;
  email?: string;
  notifications?: Notification[];
}

interface Notification {
  notificationChannel: NotificationChannel;
}

interface Flowconfig {
  biometry_capture: Biometrycapture;
}

interface Biometrycapture {
  enabled_back_camera: boolean;
}

export type UnicoCreateProcessResponse = {
  process: Process;
  selfie?: {
    fileContents: string;
    contentType: string;
  };
};

export type UnicoDocumentProcessResponse = {
  documentFrontBase64: string;
  documentBackBase64: string;
  /** Present when Unico returned `reused_doc: true` — mutually exclusive with populated front/back split. */
  fullDocumentBase64?: string;
  fullDocumentContentType?: string;
};

interface Process {
  id: string;
  flow: string;
  callbackUri: string;
  userRedirectUrl: string;
  state: string;
  result: string;
  createdAt: string;
  finishedAt?: string;
  person: Person;
  purpose: string;
  services: PersonResponse[] | UnicoProcessService[];
  authenticationInfo: AuthenticationInfo;
  capacities: string[];
  expiresAt: string;
  token: string;
  companyData: CompanyData;
  webAppToken?: string;
  bioTokenData?: {
    referenceProcessId?: string;
    authenticationId?: string;
  };
  clientReference?: string;
  useCase?: string;
}

interface CompanyData {
  branchId: string;
  countryCode: string;
}

interface ScoreEngineResult {
  scoreEnabled: string;
  score: number;
}

interface AuthenticationInfo {
  scoreEngineResult: ScoreEngineResult;
  authenticationResult: string;
  authenticationId: string;
  livenessResult: string;
  bioTokenEngineResult: string;
  trustResult: string;
  identityFraudstersResult: string;
  faceWithOtherIdResult: string;
  frictionlessResult: string;
  riskLevelResult: string;
  smartRevalidationResult: string;
}

interface PersonResponse {
  documents: Document[];
  duiType: DuiType;
  duiValue: string;
  friendlyName: string;
  email: string;
  phone: string;
  notifications: Notification[];
  phoneCountryCodeAlpha3: string;
}

// Document structure in getProcess response
export interface UnicoProcessDocument {
  doc: {
    version?: number;
    code?: string;
    data?: Record<string, unknown>;
  };
  doc_id?: string;
  signed_url?: string;
  typified?: boolean;
  cpf_match?: boolean;
  face_match?: boolean;
  validate_doc?: boolean;
  reused_doc?: boolean;
}

// Service structure in getProcess response
export interface UnicoProcessService {
  documents: UnicoProcessDocument[];
  consent_granted?: boolean;
  envelopeId?: string;
  documentIds?: string[];
}

// Selfie response structure
export interface UnicoGetSelfieResponse {
  fileContents: string;
  contentType: string;
}

/** GET /process/:id/evidenceset — probatory PDF (same shape as selfie payload) */
export type UnicoEvidenceSetResponse = UnicoGetSelfieResponse;
